import React, { useState, useEffect } from 'react';
import {
  Paper,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Alert,
  AlertTitle,
  Tabs,
  Tab,
  Tooltip,
  Card,
  CardContent,
  InputAdornment,
} from '@mui/material';
import {
  Add,
  Edit,
  Delete,
  Business,
  Category,
  Search,
  TrendingUp,
  Assignment,
  Settings,
  Palette,
  CheckCircle,
} from '@mui/icons-material';
import { apiService } from '../services/api';
import timezoneService from '../services/timezoneService';
import { useBrandColors } from '../hooks/useBrandColors';
import { PersonalizationSettings } from './PersonalizationSettings';

interface Client {
  id: string;
  name: string;
  email?: string;
  company?: string;
  phone?: string;
  address?: string;
  createdAt: string;
  updatedAt: string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`manage-tabpanel-${index}`}
      aria-labelledby={`manage-tab-${index}`}
      {...other}
      style={{ height: value === index ? 'auto' : 0, overflow: 'hidden' }}
    >
      {value === index && (
        <Box sx={{ pt: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

export const ManageSettings: React.FC = () => {
  const brandColors = useBrandColors();
  const [tabValue, setTabValue] = useState(0);
  const [categories, setCategories] = useState<string[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [projects, setProjects] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  // Search and filter states
  const [categorySearch, setCategorySearch] = useState('');
  const [clientSearch, setClientSearch] = useState('');

  // Category dialog state
  const [categoryDialog, setCategoryDialog] = useState<{
    open: boolean;
    categoryName: string;
    isEdit: boolean;
    originalName?: string;
  }>({ open: false, categoryName: '', isEdit: false });

  // Client dialog state
  const [clientDialog, setClientDialog] = useState<{
    open: boolean;
    client: Partial<Client> | null;
    isEdit: boolean;
  }>({ open: false, client: null, isEdit: false });

  // Predefined tasks organized by project type
  const predefinedTasks = [
    { value: 'development', label: 'Development', color: 'primary', projectTypes: ['coding', 'software', 'web'] },
    { value: 'design', label: 'Design', color: 'warning', projectTypes: ['design', 'ui', 'ux', 'creative'] },
    { value: 'research', label: 'Research', color: 'success', projectTypes: ['research', 'analysis', 'planning'] },
    { value: 'meeting', label: 'Meeting', color: 'secondary', projectTypes: ['all'] },
    { value: 'documentation', label: 'Documentation', color: 'info', projectTypes: ['coding', 'software', 'research'] },
    { value: 'testing', label: 'Testing', color: 'error', projectTypes: ['coding', 'software'] },
    { value: 'planning', label: 'Planning', color: 'success', projectTypes: ['all'] },
    { value: 'review', label: 'Code Review', color: 'primary', projectTypes: ['coding', 'software'] },
    { value: 'deployment', label: 'Deployment', color: 'warning', projectTypes: ['coding', 'software', 'web'] },
    { value: 'admin', label: 'Administration', color: 'default', projectTypes: ['all'] },
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [categoriesData, projectsData, clientsData] = await Promise.all([
        apiService.getCategories(),
        apiService.getProjects(),
        apiService.getClients()
      ]);
      
      setCategories(categoriesData || []);
      setProjects(projectsData || []);
      setClients(clientsData || []);
    } catch (error) {
      console.error('❌ Failed to load data:', error);
      // Set some fallback data so user can see the interface works
      setCategories(['development', 'design', 'research', 'meeting', 'documentation', 'admin', 'general']);
      setProjects([]);
      setClients([{
        id: 'sample-client',
        name: 'Sample Client',
        email: 'client@example.com',
        company: 'Example Company',
        phone: '+1-555-0123',
        address: '123 Main St, City, State 12345',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getCategoryColor = (categoryType: string) => {
    const category = predefinedTasks.find(cat => cat.value === categoryType);
    if (category) {
      return category.color as 'primary' | 'secondary' | 'success' | 'warning' | 'error';
    }
    return 'default';
  };

  // Category management
  const handleAddCategory = () => {
    setCategoryDialog({ open: true, categoryName: '', isEdit: false });
  };

  const handleEditCategory = (categoryName: string) => {
    setCategoryDialog({ 
      open: true, 
      categoryName: categoryName, 
      isEdit: true, 
      originalName: categoryName 
    });
  };

  const handleSaveCategory = async () => {
    if (!categoryDialog.categoryName.trim()) return;

    const cleanCategoryName = categoryDialog.categoryName.toLowerCase().replace(/\s+/g, '');
    
    if (categories.includes(cleanCategoryName) && cleanCategoryName !== categoryDialog.originalName) {
      alert(`Category "${cleanCategoryName}" already exists!`);
      return;
    }

    try {
      if (categoryDialog.isEdit && categoryDialog.originalName) {
        const isPredefined = predefinedTasks.some(pc => pc.value === categoryDialog.originalName);
        if (isPredefined) {
          alert(`Editing predefined categories is not yet implemented.`);
          return;
        }
        alert(`Category editing for custom categories will be implemented in a future update.`);
        return;
      } else {
        await apiService.createCategory(cleanCategoryName);
        await loadData();
        setCategoryDialog({ open: false, categoryName: '', isEdit: false });
      }
    } catch (error) {
      console.error('Failed to save category:', error);
      alert(`Failed to save category: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteCategory = async (categoryName: string) => {
    const projectCount = projects.filter((p: any) => p.categoryType === categoryName).length;
    
    if (projectCount > 0) {
      alert(`Cannot delete category "${categoryName}" because it's used by ${projectCount} project(s).`);
      return;
    }

    if (window.confirm(`Are you sure you want to delete the category "${categoryName}"?`)) {
      try {
        await apiService.deleteCategory(categoryName);
        await loadData();
      } catch (error) {
        console.error('Failed to delete category:', error);
        alert(`Failed to delete category: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  // Client management
  const handleAddClient = () => {
    setClientDialog({
      open: true,
      client: { name: '', email: '', company: '', phone: '', address: '' },
      isEdit: false,
    });
  };

  const handleEditClient = (client: Client) => {
    setClientDialog({
      open: true,
      client: { ...client },
      isEdit: true,
    });
  };

  const handleSaveClient = async () => {
    if (!clientDialog.client?.name) return;

    try {
      if (clientDialog.isEdit && clientDialog.client.id) {
        await apiService.updateClient(clientDialog.client.id, {
          name: clientDialog.client.name,
          email: clientDialog.client.email,
          company: clientDialog.client.company,
          phone: clientDialog.client.phone,
          address: clientDialog.client.address,
        });
      } else {
        await apiService.createClient({
          name: clientDialog.client.name,
          email: clientDialog.client.email,
          company: clientDialog.client.company,
          phone: clientDialog.client.phone,
          address: clientDialog.client.address,
        });
      }
      setClientDialog({ open: false, client: null, isEdit: false });
      await loadData();
    } catch (error) {
      console.error('Failed to save client:', error);
      alert(`Failed to save client: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
  };

  const handleDeleteClient = async (clientId: string) => {
    if (window.confirm('Are you sure you want to delete this client?')) {
      try {
        await apiService.deleteClient(clientId);
        await loadData();
      } catch (error) {
        console.error('Failed to delete client:', error);
        alert(`Failed to delete client: ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }
  };

  // Filter functions
  const filteredCategories = categories.filter((category: string) =>
    category.toLowerCase().includes(categorySearch.toLowerCase())
  );

  const filteredClients = clients.filter((client: Client) =>
    client.name.toLowerCase().includes(clientSearch.toLowerCase()) ||
    (client.company && client.company.toLowerCase().includes(clientSearch.toLowerCase())) ||
    (client.email && client.email.toLowerCase().includes(clientSearch.toLowerCase()))
  );

  // Statistics
  const categoryStats = {
    total: categories.length,
    custom: categories.filter((cat: string) => !predefinedTasks.some(pc => pc.value === cat)).length,
    predefined: categories.filter((cat: string) => predefinedTasks.some(pc => pc.value === cat)).length,
    inUse: categories.filter((cat: string) => projects.some((p: any) => p.categoryType === cat)).length
  };

  const clientStats = {
    total: clients.length,
    withProjects: clients.filter((client: Client) => 
      projects.some((p: any) => p.clientId === client.id)
    ).length,
    recentlyAdded: clients.filter((client: Client) => {
      const createdDate = new Date(client.createdAt);
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      return createdDate > weekAgo;
    }).length
  };

  const styles = {
    container: {
      backgroundColor: 'white',
      minHeight: '100vh',
      padding: '24px'
    },
    header: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '32px'
    },
    title: {
      fontSize: '28px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    subtitle: {
      fontSize: '16px',
      color: '#666',
      marginBottom: '24px'
    },
    metricsGridSmall: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '24px',
      marginBottom: '24px'
    },
    metricCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      display: 'flex',
      flexDirection: 'column' as const,
      backgroundColor: 'white',
      border: '1px solid #e0e0e0'
    },
    metricHeader: {
      display: 'flex',
      alignItems: 'center',
      gap: '12px',
      marginBottom: '16px'
    },
    metricTitle: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#1a1a1a'
    },
    metricValue: {
      fontSize: '28px',
      fontWeight: '700',
      color: brandColors.primary
    },
    metricSubtext: {
      fontSize: '13px',
      color: '#666'
    },
    chartCard: {
      padding: '24px',
      borderRadius: '12px',
      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      marginBottom: '24px',
      backgroundColor: 'white',
      overflow: 'hidden',
      border: '1px solid #e0e0e0'
    },
    chartTitle: {
      fontSize: '18px',
      fontWeight: '600',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    }
  };

  if (loading) {
    return <Box p={3}><Typography>Loading settings...</Typography></Box>;
  }

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <Settings sx={{ fontSize: '32px', color: brandColors.primary }} />
        <h1 style={styles.title}>Manage Settings</h1>
      </div>
      
      <p style={styles.subtitle}>
        Administrative settings and management tools for categories, clients, and system configuration.
      </p>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} aria-label="manage settings tabs">
          <Tab 
            icon={<Assignment />} 
            label="Tasks" 
            id="manage-tab-0"
            aria-controls="manage-tabpanel-0"
          />
          <Tab 
            icon={<Business />} 
            label="Clients" 
            id="manage-tab-1"
            aria-controls="manage-tabpanel-1"
          />
          <Tab 
            icon={<Palette />} 
            label="Personalization" 
            id="manage-tab-2"
            aria-controls="manage-tabpanel-2"
          />
        </Tabs>
      </Box>

      {/* Tasks Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Overview Cards */}
        <div style={styles.metricsGridSmall}>
          <Card sx={styles.metricCard}>
            <div style={styles.metricHeader}>
              <Assignment sx={{ fontSize: '24px', color: brandColors.primary }} />
              <Typography style={styles.metricTitle}>Total Tasks</Typography>
            </div>
            <Typography style={styles.metricValue}>{categoryStats.total}</Typography>
          </Card>

          <Card sx={styles.metricCard}>
             <div style={styles.metricHeader}>
               <TrendingUp sx={{ fontSize: '24px', color: '#f39c12' }} />
               <Typography style={styles.metricTitle}>In Use</Typography>
             </div>
             <Typography style={{ ...styles.metricValue, color: '#f39c12' }}>{categoryStats.inUse}</Typography>
          </Card>

          <Card sx={styles.metricCard}>
            <div style={styles.metricHeader}>
              <Category sx={{ fontSize: '24px', color: '#27ae60' }} />
              <Typography style={styles.metricTitle}>Predefined</Typography>
            </div>
            <Typography style={{ ...styles.metricValue, color: '#27ae60' }}>{categoryStats.predefined}</Typography>
          </Card>

           <Card sx={styles.metricCard}>
            <div style={styles.metricHeader}>
              <Edit sx={{ fontSize: '24px', color: '#17a2b8' }} />
              <Typography style={styles.metricTitle}>Custom</Typography>
            </div>
            <Typography style={{ ...styles.metricValue, color: '#17a2b8' }}>{categoryStats.custom}</Typography>
          </Card>
        </div>

        {/* Task List Card */}
        <Card sx={styles.chartCard}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <Assignment sx={{ color: brandColors.primary }} />
               <Typography style={styles.chartTitle} sx={{ mb: 0 }}>Task Management</Typography>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
               <TextField
                  placeholder="Search tasks..."
                  variant="outlined"
                  size="small"
                  value={categorySearch}
                  onChange={(e: any) => setCategorySearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 250 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddCategory}
                  sx={{
                    backgroundColor: '#27ae60',
                    '&:hover': {
                      backgroundColor: '#219a52',
                    },
                  }}
                >
                  Add Task
                </Button>
            </div>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Task Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Type</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Projects Using</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredCategories.map((category: string) => {
                  const projectCount = projects.filter((p: any) => p.categoryType === category).length;
                  const isPredefined = predefinedTasks.some(pc => pc.value === category);
                  
                  return (
                    <TableRow key={category} hover>
                      <TableCell>
                        <Chip
                          label={category}
                          color={getCategoryColor(category)}
                          variant="outlined"
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={isPredefined ? 'Predefined' : 'Custom'}
                          size="small"
                          color={isPredefined ? 'primary' : 'default'}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                         <Typography variant="body2" color={projectCount > 0 ? 'text.primary' : 'text.secondary'}>
                            {projectCount} project(s)
                         </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                          <Tooltip title="Edit category" arrow>
                            <IconButton
                              size="small"
                              onClick={() => handleEditCategory(category)}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {!isPredefined && projectCount === 0 && (
                            <Tooltip title="Delete category" arrow>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleDeleteCategory(category)}
                              >
                                <Delete fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
                {filteredCategories.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                       <Typography color="text.secondary">No tasks found matching your search.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
          <AlertTitle>About Tasks</AlertTitle>
          Tasks help organize your projects and time tracking. Smart predefined tasks are automatically suggested based on project type.
        </Alert>
      </TabPanel>

      {/* Clients Tab */}
      <TabPanel value={tabValue} index={1}>
         {/* Client Overview */}
         <div style={styles.metricsGridSmall}>
          <Card sx={styles.metricCard}>
            <div style={styles.metricHeader}>
               <Business sx={{ fontSize: '24px', color: brandColors.primary }} />
               <Typography style={styles.metricTitle}>Total Clients</Typography>
            </div>
            <Typography style={styles.metricValue}>{clientStats.total}</Typography>
          </Card>

          <Card sx={styles.metricCard}>
             <div style={styles.metricHeader}>
               <Assignment sx={{ fontSize: '24px', color: '#27ae60' }} />
               <Typography style={styles.metricTitle}>Active Clients</Typography>
             </div>
             <Typography style={{ ...styles.metricValue, color: '#27ae60' }}>{clientStats.withProjects}</Typography>
             <Typography style={styles.metricSubtext}>With projects</Typography>
          </Card>

          <Card sx={styles.metricCard}>
             <div style={styles.metricHeader}>
               <TrendingUp sx={{ fontSize: '24px', color: '#f39c12' }} />
               <Typography style={styles.metricTitle}>New Clients</Typography>
             </div>
             <Typography style={{ ...styles.metricValue, color: '#f39c12' }}>{clientStats.recentlyAdded}</Typography>
             <Typography style={styles.metricSubtext}>Added this week</Typography>
          </Card>
        </div>

        {/* Client List Card */}
        <Card sx={styles.chartCard}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
             <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
               <Business sx={{ color: brandColors.primary }} />
               <Typography style={styles.chartTitle} sx={{ mb: 0 }}>Client Management</Typography>
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
               <TextField
                  placeholder="Search clients..."
                  variant="outlined"
                  size="small"
                  value={clientSearch}
                  onChange={(e: any) => setClientSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                         <Search sx={{ color: 'text.secondary' }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ minWidth: 300 }}
                />
                <Button
                  variant="contained"
                  startIcon={<Add />}
                  onClick={handleAddClient}
                  sx={{
                    backgroundColor: '#3498db',
                    '&:hover': {
                      backgroundColor: '#2980b9',
                    },
                  }}
                >
                  Add Client
                </Button>
            </div>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow sx={{ backgroundColor: '#f8f9fa' }}>
                  <TableCell sx={{ fontWeight: 600 }}>Client Name</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Company</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Phone</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Created</TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredClients.map((client: Client) => (
                  <TableRow key={client.id} hover>
                    <TableCell>
                      <Typography variant="subtitle2" sx={{ fontWeight: 500 }}>
                        {client.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{client.company || '-'}</TableCell>
                    <TableCell>{client.email || '-'}</TableCell>
                    <TableCell>{client.phone || '-'}</TableCell>
                    <TableCell>
                      {client.createdAt ? (
                        timezoneService.formatUserDate(
                          timezoneService.convertUTCToUserTime(client.createdAt)
                        )
                      ) : 'N/A'}
                    </TableCell>
                    <TableCell>
                      <Tooltip title="Edit client">
                        <IconButton
                          size="small"
                          onClick={() => handleEditClient(client)}
                        >
                          <Edit fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete client">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleDeleteClient(client.id)}
                        >
                          <Delete fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
                {filteredClients.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 6 }}>
                      <Box sx={{ textAlign: 'center' }}>
                        <Business sx={{ fontSize: 48, color: 'text.secondary', mb: 2, opacity: 0.5 }} />
                        <Typography variant="h6" sx={{ mb: 1, fontWeight: 600, color: 'text.secondary' }}>
                           No clients found
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                           {clientSearch ? `No results for "${clientSearch}"` : "Add your first client to get started."}
                        </Typography>
                      </Box>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>
      </TabPanel>

      {/* Personalization Tab */}
      <TabPanel value={tabValue} index={2}>
        <Box sx={{ mb: 3 }}>
          <Alert severity="info" icon={<Palette sx={{ fontSize: 22 }} />} sx={{ borderRadius: 2 }}>
             <AlertTitle>Customize Your Experience</AlertTitle>
             Changes made here will update the look and feel of the entire application immediately.
          </Alert>
        </Box>
        <PersonalizationSettings />
      </TabPanel>

      {/* Add/Edit Category Dialog */}
      <Dialog
        open={categoryDialog.open}
        onClose={() => setCategoryDialog({ open: false, categoryName: '', isEdit: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {categoryDialog.isEdit ? 'Edit Category' : 'Add New Category'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Category Name"
              required
              fullWidth
              value={categoryDialog.categoryName}
              placeholder="Enter category name (e.g., marketing, sales)"
              onChange={(e) =>
                setCategoryDialog({
                  ...categoryDialog,
                  categoryName: e.target.value,
                })
              }
              helperText="Category will be formatted as lowercase with no spaces"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCategoryDialog({ open: false, categoryName: '', isEdit: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveCategory} 
            variant="contained"
            disabled={!categoryDialog.categoryName.trim()}
          >
            {categoryDialog.isEdit ? 'Update Category' : 'Create Category'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add/Edit Client Dialog */}
      <Dialog
        open={clientDialog.open}
        onClose={() => setClientDialog({ open: false, client: null, isEdit: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {clientDialog.isEdit ? 'Edit Client' : 'Add New Client'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Client Name"
              required
              fullWidth
              value={clientDialog.client?.name || ''}
              onChange={(e: any) =>
                setClientDialog({
                  ...clientDialog,
                  client: clientDialog.client
                    ? { ...clientDialog.client, name: e.target.value }
                    : null,
                })
              }
            />
            <TextField
              label="Company"
              fullWidth
              value={clientDialog.client?.company || ''}
              onChange={(e) =>
                setClientDialog({
                  ...clientDialog,
                  client: clientDialog.client
                    ? { ...clientDialog.client, company: e.target.value }
                    : null,
                })
              }
            />
            <TextField
              label="Email"
              type="email"
              fullWidth
              value={clientDialog.client?.email || ''}
              onChange={(e) =>
                setClientDialog({
                  ...clientDialog,
                  client: clientDialog.client
                    ? { ...clientDialog.client, email: e.target.value }
                    : null,
                })
              }
            />
            <TextField
              label="Phone"
              fullWidth
              value={clientDialog.client?.phone || ''}
              onChange={(e) =>
                setClientDialog({
                  ...clientDialog,
                  client: clientDialog.client
                    ? { ...clientDialog.client, phone: e.target.value }
                    : null,
                })
              }
            />
            <TextField
              label="Address"
              multiline
              rows={3}
              fullWidth
              value={clientDialog.client?.address || ''}
              onChange={(e) =>
                setClientDialog({
                  ...clientDialog,
                  client: clientDialog.client
                    ? { ...clientDialog.client, address: e.target.value }
                    : null,
                })
              }
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setClientDialog({ open: false, client: null, isEdit: false })}>
            Cancel
          </Button>
          <Button 
            onClick={handleSaveClient} 
            variant="contained"
            disabled={!clientDialog.client?.name}
          >
            {clientDialog.isEdit ? 'Update' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};
